// Feature #286 — Reviews Pending: the sm-manager inbox. Open deltas
// grouped by person (awaiting roll-up) plus reviews already in progress.
import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/ui/PageHeader'
import { formatDateTime } from '@/utils/dateFormat'
import { getReviewsPending, SmDelta, SmReviewsPending } from '@/api/sm'
import { DeltaList } from './components/SmShared'

const ReviewsPending: React.FC = () => {
  const navigate = useNavigate()
  const [data, setData] = useState<SmReviewsPending | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getReviewsPending()
      .then(setData)
      .catch((e) => setError(e?.message || 'Failed to load inbox'))
  }, [])

  const byPerson = useMemo(() => {
    const groups = new Map<string, { name: string; deltas: SmDelta[] }>()
    for (const d of data?.open_deltas || []) {
      const group = groups.get(d.user_id) || {
        name: d.user_display_name || d.user_id,
        deltas: [],
      }
      group.deltas.push(d)
      groups.set(d.user_id, group)
    }
    return groups
  }, [data])

  return (
    <div>
      <PageHeader title="Reviews Pending" />
      <div style={{ padding: '24px', display: 'grid', gap: '32px' }}>
        {error && <div style={{ color: '#b91c1c' }}>{error}</div>}
        {!data && !error && <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>}
        {data && (
          <>
            <section>
              <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>
                Open deltas awaiting review
              </h2>
              {byPerson.size === 0 && (
                <p style={{ color: 'var(--color-dark-grey)' }}>No open deltas — all caught up.</p>
              )}
              <div style={{ display: 'grid', gap: '16px' }}>
                {Array.from(byPerson.entries()).map(([userId, group]) => (
                  <div
                    key={userId}
                    style={{
                      border: '1px solid var(--color-light-grey)',
                      borderRadius: '8px',
                      padding: '16px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                      }}
                    >
                      <strong>{group.name}</strong>
                      <Link
                        to={`/sm/staff/${userId}`}
                        style={{ color: 'var(--color-magenta)', fontSize: '14px', textDecoration: 'none' }}
                      >
                        Select deltas &amp; open review →
                      </Link>
                    </div>
                    <DeltaList deltas={group.deltas} />
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>
                Reviews in progress
              </h2>
              {data.reviews_in_progress.length === 0 && (
                <p style={{ color: 'var(--color-dark-grey)' }}>No reviews in progress.</p>
              )}
              <div style={{ display: 'grid', gap: '8px' }}>
                {data.reviews_in_progress.map((r) => (
                  <div
                    key={r.id}
                    onClick={() => navigate(`/sm/reviews/${r.id}`)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      border: '1px solid var(--color-light-grey)',
                      borderLeft: '4px solid #2563eb',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    <span>
                      <strong>{r.user_display_name || r.user_id}</strong> — {r.deltas.length}{' '}
                      delta(s)
                    </span>
                    <span style={{ color: 'var(--color-dark-grey)' }}>
                      opened {formatDateTime(r.opened_at)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  )
}

export default ReviewsPending
